import React, { useState } from 'react';
import { Layout, Menu, Button, theme, Space, message, Input } from 'antd';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useThemeSettings } from '../contexts/ThemeContext';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    SearchOutlined,
    DashboardOutlined,
    EnvironmentOutlined,
    FileTextOutlined,
    SettingOutlined,
    LogoutOutlined,
    PictureOutlined,
    MenuOutlined,
    NotificationOutlined,
    BookOutlined,
    FolderOutlined,
    LayoutOutlined,
    GlobalOutlined,
    ClearOutlined,
    ExceptionOutlined,
    PlaySquareOutlined,
    HomeOutlined,
    BankOutlined,
    UserOutlined,
    DatabaseOutlined,
    LockOutlined,
    MailOutlined
} from '@ant-design/icons';

const { Header, Sider, Content } = Layout;

export const AdminLayout: React.FC = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const { isAuthenticated, logout } = useAuth();
    const { siteTokens } = useThemeSettings();
    const location = useLocation();

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    if (!isAuthenticated) {
        return <Navigate to="/admin/login" replace />;
    }

    const menuItems: any[] = [
        { key: '/admin', icon: <DashboardOutlined />, label: <Link to="/admin">Dashboard</Link> },
        {
            type: 'group',
            label: 'İçerik Yönetimi',
            children: [
                { key: '/admin/pages', icon: <FileTextOutlined />, label: <Link to="/admin/pages">Sayfalar</Link> },
                { key: '/admin/homepage-settings', icon: <HomeOutlined />, label: <Link to="/admin/homepage-settings">Anasayfa Ayarları</Link> },
                { key: '/admin/sliders', icon: <PlaySquareOutlined />, label: <Link to="/admin/sliders">Sliderlar</Link> },
                { key: '/admin/posters', icon: <NotificationOutlined />, label: <Link to="/admin/posters">Etkinlikler / Posterler</Link> },
                { key: '/admin/courses', icon: <BookOutlined />, label: <Link to="/admin/courses">Kurslar</Link> },
                { key: '/admin/projects', icon: <FolderOutlined />, label: <Link to="/admin/projects">Projeler</Link> },
                { key: '/admin/affiliations', icon: <GlobalOutlined />, label: <Link to="/admin/affiliations">Ortak / Bağlı Dernekler</Link> },
                { key: '/admin/newsletters', icon: <MailOutlined />, label: <Link to="/admin/newsletters">E-Bültenler (Newsletters)</Link> },
                { key: '/admin/forms', icon: <FileTextOutlined />, label: <Link to="/admin/forms">Özel Formlar</Link> },
            ]
        },
        {
            type: 'group',
            label: 'Etkileşim & Bağış',
            children: [
                { key: '/admin/contact-info', icon: <EnvironmentOutlined />, label: <Link to="/admin/contact-info">İletişim Bilgileri</Link> },
                { key: '/admin/contact-messages', icon: <FileTextOutlined />, label: <Link to="/admin/contact-messages">İletişim Mesajları</Link> },
                { key: '/admin/donate-settings', icon: <BankOutlined />, label: <Link to="/admin/donate-settings">Bağış Ayarları</Link> },
                { key: '/admin/donations', icon: <BankOutlined />, label: <Link to="/admin/donations">Gelen Bağışlar</Link> },
            ]
        },
        {
            type: 'group',
            label: 'OLE Lernzentrum',
            children: [
                { key: '/admin/ole/pages', icon: <FileTextOutlined />, label: <Link to="/admin/ole/pages">Sayfalar</Link> },
                { key: '/admin/ole/menus', icon: <MenuOutlined />, label: <Link to="/admin/ole/menus">Menüler</Link> },
                { key: '/admin/ole/sliders', icon: <PictureOutlined />, label: <Link to="/admin/ole/sliders">Slider Görselleri</Link> },
                { key: '/admin/ole/contact-info', icon: <EnvironmentOutlined />, label: <Link to="/admin/ole/contact-info">İletişim Bilgileri</Link> },
                { key: '/admin/ole/contact-messages', icon: <FileTextOutlined />, label: <Link to="/admin/ole/contact-messages">İletişim Mesajları</Link> },
                { key: '/admin/ole/site-config', icon: <SettingOutlined />, label: <Link to="/admin/ole/site-config">Site Ayarları</Link> },
            ]
        },
        {
            type: 'group',
            label: 'Sistem & Teknik',
            children: [
                { key: '/admin/media', icon: <PictureOutlined />, label: <Link to="/admin/media">Medya Kütüphanesi</Link> },
                { key: '/admin/media/webp-job', icon: <PictureOutlined />, label: <Link to="/admin/media/webp-job">WebP Dönüştürücü</Link> },
                { key: '/admin/menus', icon: <MenuOutlined />, label: <Link to="/admin/menus">Menü Yönetimi</Link> },
                { key: '/admin/page-templates', icon: <LayoutOutlined />, label: <Link to="/admin/page-templates">Sayfa Şablonları</Link> },
                { key: '/admin/footer-settings', icon: <PlaySquareOutlined />, label: <Link to="/admin/footer-settings">Footer Ayarları</Link> },
                { key: '/admin/settings', icon: <SettingOutlined />, label: <Link to="/admin/settings">Genel Ayarlar</Link> },
                { key: '/admin/translations', icon: <GlobalOutlined />, label: <Link to="/admin/translations">Dil / Çeviri Yönetimi</Link> },
                { key: '/admin/users', icon: <UserOutlined />, label: <Link to="/admin/users">Kullanıcı Yönetimi</Link> },
                { key: '/admin/login-logs', icon: <LockOutlined />, label: <Link to="/admin/login-logs">Giriş Denemeleri Günlüğü</Link> },
                { key: '/admin/notifications', icon: <NotificationOutlined />, label: <Link to="/admin/notifications">Bildirim Kuyruğu</Link> },
                { 
                    key: 'hangfire', 
                    icon: <DatabaseOutlined />, 
                    label: <a href={`${(apiClient.defaults.baseURL as string)?.replace('/api', '')}/admin/hangfire`} target="_blank" rel="noopener noreferrer">Arka Plan İşleri (Hangfire)</a> 
                },
                { key: '/admin/logs', icon: <ExceptionOutlined />, label: <Link to="/admin/logs">Sistem Günlükleri</Link> },
            ]
        },
    ];

    const filterMenuItems = (items: any[]): any[] => {
        return items
            .map(item => {
                if (item.type === 'group') {
                    const filteredChildren = filterMenuItems(item.children || []);
                    if (filteredChildren.length > 0) {
                        return { ...item, children: filteredChildren };
                    }
                    return null;
                }

                // Get label string from element or string
                let labelText = '';
                if (React.isValidElement(item.label)) {
                    // Handle <Link>Text</Link> or similar
                    const props = item.label.props as any;
                    labelText = typeof props.children === 'string' ? props.children : '';
                } else if (typeof item.label === 'string') {
                    labelText = item.label;
                }

                if (labelText.toLowerCase().includes(searchTerm.toLowerCase())) {
                    return item;
                }
                return null;
            })
            .filter(item => item !== null);
    };

    const filteredMenuItems = searchTerm ? filterMenuItems(menuItems) : menuItems;

    const handleClearCache = async () => {
        try {
            await apiClient.post('/admin/settings/clear-cache');
            message.success('Backend cache cleared successfully');
        } catch (error) {
            console.error('Failed to clear cache:', error);
            message.error('Failed to clear cache');
        }
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider trigger={null} collapsible collapsed={collapsed}>
                <div style={{ height: 60, margin: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {siteTokens.LogoUrl ? (
                         <img 
                            src={siteTokens.LogoUrl} 
                            alt="Logo" 
                            style={{ 
                                maxHeight: '100%', 
                                maxWidth: '100%', 
                                objectFit: 'contain'
                            }} 
                        />
                    ) : (
                        <div style={{ color: 'white', fontWeight: 'bold' }}>{collapsed ? 'M' : (siteTokens.SiteName || 'Mosaik')}</div>
                    )}
                </div>
                {!collapsed && (
                    <div style={{ padding: '0 16px 16px 16px' }}>
                        <Input
                            placeholder="Menüde ara..."
                            prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,.45)' }} />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="filled"
                            style={{ 
                                backgroundColor: 'rgba(255,255,255,0.1)', 
                                border: 'none',
                                color: 'white'
                            }}
                            allowClear
                        />
                    </div>
                )}
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={filteredMenuItems}
                />
            </Sider>
            <Layout>
                <Header style={{ padding: 0, background: colorBgContainer, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        style={{ fontSize: '16px', width: 64, height: 64 }}
                    />
                    <Space size="middle">
                        <Button type="link" icon={<GlobalOutlined />} href="/" target="_blank">
                            Public Site
                        </Button>
                        <Button onClick={handleClearCache} icon={<ClearOutlined />}>
                            Clear Cache
                        </Button>
                        <Button type="text" danger icon={<LogoutOutlined />} onClick={logout}>
                            Logout
                        </Button>
                    </Space>
                </Header>
                <Content
                    style={{
                        margin: '24px 16px',
                        padding: 24,
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                        minHeight: 280,
                    }}
                >
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};
